import { Node, mergeAttributes } from '@tiptap/core';
import { VueNodeViewRenderer } from '@tiptap/vue-3';

import AttachmentFileNode from './AttachmentFileNode.vue';
import { typeAfterSelectedNode } from './caret';

export interface AttachmentFileAttrs {
  /** BinaryFile id — null while the upload is still in flight. */
  fileId: string | null;
  /** Transient upload correlation id; cleared when the upload finalizes. */
  uploadId: string | null;
  name: string;
  size: number;
  mimeType: string;
}

/**
 * File card embedded in the rich-text body. Persists only the
 * BinaryFile id + display meta — presigned URLs are resolved at render time
 * (see useBinaryFileHydration).
 */
export const AttachmentFile = Node.create({
  name: 'attachmentFile',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      fileId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-file-id'),
        renderHTML: (attributes) => (attributes.fileId ? { 'data-file-id': attributes.fileId } : {}),
      },
      uploadId: {
        default: null,
        // Never serialized to HTML — only meaningful while the tab that
        // started the upload is alive.
        renderHTML: () => ({}),
      },
      name: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-name') ?? '',
        renderHTML: (attributes) => (attributes.name ? { 'data-name': attributes.name } : {}),
      },
      size: {
        default: 0,
        parseHTML: (element) => Number(element.getAttribute('data-size')) || 0,
        renderHTML: (attributes) => (attributes.size ? { 'data-size': String(attributes.size) } : {}),
      },
      mimeType: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-mime-type') ?? '',
        renderHTML: (attributes) => (attributes.mimeType ? { 'data-mime-type': attributes.mimeType } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="attachment-file"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': 'attachment-file' }, HTMLAttributes)];
  },

  addNodeView() {
    return VueNodeViewRenderer(AttachmentFileNode);
  },

  addProseMirrorPlugins() {
    return [typeAfterSelectedNode(this.type)];
  },
});
